import * as v0 from '@aidkitorg/types/lib/survey';
import { useContext } from 'react';
import InterfaceContext, { SupportedLanguage } from '../Context';
import { InfoDict } from '../Questions/Props';
import { safeParse, useMarkdown } from '../Util';
import { sanitizeDangerousText } from '@aidkitorg/types/lib/utils/textSanitizer';
import buildConditionalContent from '@aidkitorg/types/lib/utils/conditionalContent';
import { useMemo } from 'react';
import { FIELD_REF_REGEX, infoReplacer } from '@aidkitorg/types/lib/util';

export function toMarkdownWithReplacements(props: {
  lang: SupportedLanguage,
  content: string | v0.Content | v0.ConditionalContent, 
  info: InfoDict,
  uid?: string,
  replacer?: (str: string) => string,
  replaceWithHtml?: {
    regex: RegExp, 
    html: string
  },
  collapsible?: boolean, 
  noParagraphs?: boolean
}) {

  const pieces = buildConditionalContent({ 
    uid: props.uid, 
    info: props.info, 
    lang: props.lang,
    content: props.content
  });

  // By wrapping substitutions with <bdi> tags like this, we greatly improve the odds
  // that they are displayed in the expected direction (ltr or rtl) regardless of the surrounding text.
  // See: https://www.w3.org/International/articles/inline-bidi-markup/index
  function wrapForDirection(wrappedFunc: (str: string) => string) {
    return (s: string) => `<bdi style="white-space: break-spaces">${wrappedFunc(s)}</bdi>`;
  }

  // Replaces uid and variables in text and sanitizes and wraps with directional span before using the
  // replacer function that was passed to useModularMarkdown, if there was one
  const firstReplace = (str: string) => str.replace(FIELD_REF_REGEX, wrapForDirection(wrapped => infoReplacer(wrapped, props)));

  const replacer = props.replacer ? (str: string) => props.replacer!(firstReplace(str)) : firstReplace;

  return replacer(pieces.join("\n"));
}

/**
 * - Converts Content (Array or Object or String) to a single Markdown JSX Element
 * - Replaces any $variables with the value of props.info[variable]
 */
export function useModularMarkdown(props: {
  content: string | v0.Content | v0.ConditionalContent, 
  info: InfoDict,
  uid?: string,
  replacer?: (str: string) => string,
  replaceWithHtml?: {
    regex: RegExp, 
    html: string
  },
  collapsible?: boolean, 
  noParagraphs?: boolean
}) {
  const context = useContext(InterfaceContext);

  const { collapsible, noParagraphs } = props;

  // Only rebuild conditional content if info or lang changes
  const marked = useMemo(() => toMarkdownWithReplacements({
    lang: context.lang,
    ...props
  }), [context.lang, props.info, props.uid, props.content]);

  return useMarkdown(marked, collapsible, noParagraphs, props.replaceWithHtml);
}
